// Every control on this screen is generated from the model descriptor the server publishes
// (IMPLEMENTATION_PLAN.md §5.2, §11.1). Nothing here knows a provider, a model id or a parameter
// key: a new provider parameter is a catalog edit on the server and nothing at all here.
//
// The other half of the job is agreeing with the server's resolution rules (§5.3). If the client
// shows a control the server will reject, or sends a value the server has already decided is
// hidden, the user pays for it in avoidable 400s.

import { useId, useMemo, useState } from "react";
import type { KeyboardEvent as ReactKeyboardEvent, ReactElement } from "react";
import { Dices, Lock, Unlock } from "lucide-react";
import { parseRatio } from "../api";
import type {
  GenerationMode,
  Model,
  Parameter,
  ParameterGroup,
  ParameterOption,
  ParameterValues,
} from "../api";
import { IconButton } from "./ui";

// ── Value access ───────────────────────────────────────────────────────────────────────────

type Scalar = string | number | boolean;

/** ParameterValues has no `undefined` in its type, but an absent key is the normal case. */
function readValue(values: ParameterValues, key: string): Scalar | undefined {
  const lookup: Record<string, Scalar | undefined> = values;
  return lookup[key];
}

function toNumber(value: Scalar | undefined): number | undefined {
  if (typeof value === "number") return Number.isFinite(value) ? value : undefined;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

/**
 * `visible_when.equals_any` is a list of strings whatever the controlling parameter's kind is,
 * so comparison happens in string space on both sides — the same thing the server does.
 */
function matches(value: Scalar | undefined, equalsAny: string[]): boolean {
  if (value === undefined) return false;
  return equalsAny.includes(String(value));
}

function appliesIn(parameter: Parameter, mode: GenerationMode): boolean {
  return parameter.applies_to.includes(mode);
}

// ── Resolution (§5.3) ──────────────────────────────────────────────────────────────────────

/**
 * The set of parameter keys that are both applicable to the mode and currently visible.
 *
 * Computed to a fixpoint rather than in one pass. A rule may point at a parameter that is itself
 * governed by a rule, so hiding a parent has to hide its dependants, and theirs. Starting from
 * "everything applicable is visible" and only ever removing keys means the set shrinks
 * monotonically, so the loop terminates in at most one pass per parameter even if the catalog
 * ever contained a cycle.
 */
function resolveVisible(
  model: Model,
  mode: GenerationMode,
  values: ParameterValues,
): ReadonlySet<string> {
  const applicable = model.parameters.filter((parameter) => appliesIn(parameter, mode));
  const visible = new Set(applicable.map((parameter) => parameter.key));

  let settled = false;
  while (!settled) {
    settled = true;
    for (const parameter of applicable) {
      if (!visible.has(parameter.key)) continue;
      const rule = parameter.visible_when;
      if (!rule) continue;

      // A rule whose controlling key is itself hidden (or is not applicable in this mode) can
      // never be satisfied, because the server resolves values with hidden keys dropped.
      const satisfied = visible.has(rule.key) && matches(readValue(values, rule.key), rule.equals_any);
      if (!satisfied) {
        visible.delete(parameter.key);
        settled = false;
      }
    }
  }

  return visible;
}

/**
 * The declared defaults for a mode. A `null` default is skipped deliberately: it means "omit this
 * from the provider request", which is not the same as sending the provider's own default value.
 */
export function defaultsFor(model: Model, mode: GenerationMode): ParameterValues {
  const defaults: ParameterValues = {};
  for (const parameter of model.parameters) {
    if (!appliesIn(parameter, mode)) continue;
    const declared = parameter.default;
    if (declared === undefined || declared === null) continue;
    defaults[parameter.key] = declared;
  }
  return defaults;
}

/**
 * Strips everything the server would reject or ignore: keys the model does not declare (left over
 * from a previously selected model), keys that do not apply to this mode, and keys hidden by a
 * `visible_when` rule. A stale hidden value never reaches the provider.
 */
export function prepareForSubmit(
  model: Model,
  mode: GenerationMode,
  values: ParameterValues,
): ParameterValues {
  const visible = resolveVisible(model, mode, values);
  const submitted: ParameterValues = {};
  for (const parameter of model.parameters) {
    if (!visible.has(parameter.key)) continue;
    const value = readValue(values, parameter.key);
    if (value === undefined) continue;
    submitted[parameter.key] = value;
  }
  return submitted;
}

/** How many visible, applicable values differ from the model's declared defaults. */
export function countChangedFromDefaults(
  model: Model,
  mode: GenerationMode,
  values: ParameterValues,
): number {
  const visible = resolveVisible(model, mode, values);
  let changed = 0;

  for (const parameter of model.parameters) {
    if (!visible.has(parameter.key)) continue;
    const value = readValue(values, parameter.key);
    if (value === undefined) continue;

    const declared = parameter.default;
    if (declared === undefined || declared === null) {
      // No declared default means the server would omit the key. Holding any value is a change.
      changed += 1;
      continue;
    }
    // Compared as strings so a number that made a round trip through an input still equals its
    // own default.
    if (String(value) !== String(declared)) changed += 1;
  }

  return changed;
}

// ── Control selection (§11.1) ──────────────────────────────────────────────────────────────

type ControlShape =
  | "ratio"
  | "segmented"
  | "select"
  | "slider"
  | "number"
  | "switch"
  | "seed"
  | "text";

const SEGMENTED_MAX_OPTIONS = 5;
const SEGMENTED_MAX_LABEL = 10;

function controlShapeFor(parameter: Parameter): ControlShape {
  switch (parameter.kind) {
    case "enum": {
      const options = parameter.options ?? [];
      // A ratio is a shape, and a drawn shape is read faster than "1536x1024".
      if (options.some((option) => option.ratio)) return "ratio";
      if (
        options.length > 0 &&
        options.length <= SEGMENTED_MAX_OPTIONS &&
        options.every((option) => option.label.length <= SEGMENTED_MAX_LABEL)
      ) {
        return "segmented";
      }
      return "select";
    }
    case "integer":
    case "number":
      return parameter.min !== undefined && parameter.max !== undefined ? "slider" : "number";
    case "boolean":
      return "switch";
    case "seed":
      return "seed";
    case "text":
      return "text";
  }
}

/** The button groups own their own labelling; everything else is a single labellable control. */
function isGroupControl(shape: ControlShape): boolean {
  return shape === "ratio" || shape === "segmented";
}

function stepFor(parameter: Parameter): number | "any" {
  if (parameter.step !== undefined) return parameter.step;
  return parameter.kind === "number" ? "any" : 1;
}

// ── Ratio swatches ─────────────────────────────────────────────────────────────────────────

/** The long edge of a swatch, in pixels. The short edge follows the parsed ratio. */
const SWATCH_LONG_EDGE = 34;

function swatchBox(ratio: string | undefined): { width: number; height: number } {
  const value = parseRatio(ratio);
  if (value === null) return { width: SWATCH_LONG_EDGE, height: SWATCH_LONG_EDGE };
  // A minimum of 4px so an extreme ratio (xAI ships 9:19.5) still draws as a visible sliver.
  return value >= 1
    ? { width: SWATCH_LONG_EDGE, height: Math.max(4, Math.round(SWATCH_LONG_EDGE / value)) }
    : { width: Math.max(4, Math.round(SWATCH_LONG_EDGE * value)), height: SWATCH_LONG_EDGE };
}

// ── Keyboard ───────────────────────────────────────────────────────────────────────────────

/** Arrow-key navigation, which is what a `radiogroup` promises a screen reader user. */
function handleRadioKeys(
  event: ReactKeyboardEvent<HTMLDivElement>,
  options: ParameterOption[],
  index: number,
  select: (option: ParameterOption) => void,
): void {
  const forward = event.key === "ArrowRight" || event.key === "ArrowDown";
  const back = event.key === "ArrowLeft" || event.key === "ArrowUp";
  if ((!forward && !back) || options.length === 0) return;

  event.preventDefault();
  const next = (index + (forward ? 1 : -1) + options.length) % options.length;
  const option = options[next];
  if (!option) return;

  select(option);
  const button = event.currentTarget.querySelectorAll("button").item(next);
  if (button instanceof HTMLElement) button.focus();
}

// ── Numeric input ──────────────────────────────────────────────────────────────────────────

interface NumberFieldProps {
  parameter: Parameter;
  value: number | undefined;
  invalid: boolean;
  id?: string;
  label?: string;
  describedBy?: string;
  placeholder?: string;
  readOnly?: boolean;
  onCommit: (next: number | undefined) => void;
}

/**
 * Keeps the raw text while the field has focus. Parsing on every keystroke and echoing the parsed
 * number back would make "-" and "1e" impossible to type.
 */
function NumberField({
  parameter,
  value,
  invalid,
  id,
  label,
  describedBy,
  placeholder,
  readOnly,
  onCommit,
}: NumberFieldProps): ReactElement {
  const [draft, setDraft] = useState<string | null>(null);
  const text = draft ?? (value === undefined ? "" : String(value));

  return (
    <input
      id={id}
      aria-label={label}
      aria-describedby={describedBy}
      className={invalid ? "input is-invalid" : "input"}
      type="number"
      inputMode={parameter.kind === "number" ? "decimal" : "numeric"}
      min={parameter.min}
      max={parameter.max}
      step={stepFor(parameter)}
      value={text}
      placeholder={placeholder}
      readOnly={readOnly}
      onChange={(event) => {
        const raw = event.target.value;
        setDraft(raw);
        if (raw.trim() === "") {
          onCommit(undefined);
          return;
        }
        const parsed = Number(raw);
        if (Number.isFinite(parsed)) onCommit(parsed);
      }}
      onBlur={() => setDraft(null)}
    />
  );
}

// ── Seed ───────────────────────────────────────────────────────────────────────────────────

const SEED_CEILING = 2147483647;

function randomSeed(parameter: Parameter): number {
  const min = parameter.min ?? 0;
  const max = parameter.max ?? SEED_CEILING;
  return Math.floor(min + Math.random() * (max - min + 1));
}

interface SeedFieldProps {
  parameter: Parameter;
  value: number | undefined;
  invalid: boolean;
  id: string;
  describedBy?: string;
  onCommit: (next: number | undefined) => void;
}

function SeedField({
  parameter,
  value,
  invalid,
  id,
  describedBy,
  onCommit,
}: SeedFieldProps): ReactElement {
  // The lock is a working state of this control, not a parameter: it says "leave this seed
  // alone", which is only meaningful while the composer is open.
  const [locked, setLocked] = useState(false);

  return (
    <div className="row">
      <span className="grow">
        <NumberField
          parameter={parameter}
          value={value}
          invalid={invalid}
          id={id}
          describedBy={describedBy}
          placeholder="Random each time"
          readOnly={locked}
          onCommit={onCommit}
        />
      </span>
      <IconButton
        onClick={() => onCommit(randomSeed(parameter))}
        label="Randomise seed"
        disabled={locked}
        disabledReason="Unlock the seed before changing it."
      >
        <Dices size={15} />
      </IconButton>
      <IconButton
        onClick={() => setLocked((current) => !current)}
        label={locked ? "Unlock seed" : "Lock seed"}
      >
        {locked ? <Lock size={15} /> : <Unlock size={15} />}
      </IconButton>
    </div>
  );
}

// ── One parameter ──────────────────────────────────────────────────────────────────────────

const EXPERIMENTAL_TITLE =
  "This parameter is documented but could not be confirmed against the provider's official reference. It may be ignored or rejected.";

interface FieldProps {
  parameter: Parameter;
  value: Scalar | undefined;
  error?: string;
  onChange: (next: Scalar | undefined) => void;
}

function ParameterField({ parameter, value, error, onChange }: FieldProps): ReactElement {
  const uid = useId();
  const controlId = `${uid}-control`;
  const labelId = `${uid}-label`;
  const helpId = `${uid}-help`;
  const errorId = `${uid}-error`;

  const shape = controlShapeFor(parameter);
  const options = parameter.options ?? [];
  const describedBy =
    [parameter.description ? helpId : null, error ? errorId : null].filter(Boolean).join(" ") ||
    undefined;

  const selectedIndex = options.findIndex((option) => option.value === String(value));
  const focusIndex = selectedIndex >= 0 ? selectedIndex : 0;
  const numeric = toNumber(value);

  let control: ReactElement;

  switch (shape) {
    case "ratio":
      control = (
        <div
          className="ratio-grid"
          role="radiogroup"
          aria-labelledby={labelId}
          aria-describedby={describedBy}
          onKeyDown={(event) =>
            handleRadioKeys(event, options, focusIndex, (option) => onChange(option.value))
          }
        >
          {options.map((option, index) => {
            const selected = index === selectedIndex;
            const box = swatchBox(option.ratio);
            return (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={selected}
                tabIndex={index === focusIndex ? 0 : -1}
                className={selected ? "ratio-swatch is-selected" : "ratio-swatch"}
                title={option.hint ?? option.value}
                onClick={() => onChange(option.value)}
              >
                <span className="ratio-shape" style={{ width: box.width, height: box.height }} />
                <span className="ratio-label">{option.label}</span>
              </button>
            );
          })}
        </div>
      );
      break;

    case "segmented":
      control = (
        <div
          className="segmented"
          role="radiogroup"
          aria-labelledby={labelId}
          aria-describedby={describedBy}
          onKeyDown={(event) =>
            handleRadioKeys(event, options, focusIndex, (option) => onChange(option.value))
          }
        >
          {options.map((option, index) => {
            const selected = index === selectedIndex;
            return (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={selected}
                tabIndex={index === focusIndex ? 0 : -1}
                className={selected ? "is-selected" : undefined}
                title={option.hint ?? option.value}
                onClick={() => onChange(option.value)}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      );
      break;

    case "select":
      control = (
        <select
          id={controlId}
          className={error ? "select is-invalid" : "select"}
          aria-describedby={describedBy}
          value={selectedIndex >= 0 ? String(value) : ""}
          onChange={(event) => onChange(event.target.value === "" ? undefined : event.target.value)}
        >
          {/* Offered where the model declares no default, so "leave it to the provider" stays
              reachable once a value has been chosen — and whenever nothing is selected, so the
              select always has an option matching its own value. */}
          {selectedIndex < 0 || parameter.default === undefined || parameter.default === null ? (
            <option value="">Not set</option>
          ) : null}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.hint ? `${option.label} — ${option.hint}` : option.label}
            </option>
          ))}
        </select>
      );
      break;

    case "slider":
      control = (
        <div className="slider-row">
          <input
            id={controlId}
            type="range"
            aria-describedby={describedBy}
            min={parameter.min}
            max={parameter.max}
            step={stepFor(parameter)}
            value={numeric ?? parameter.min ?? 0}
            onChange={(event) => onChange(Number(event.target.value))}
          />
          <NumberField
            parameter={parameter}
            value={numeric}
            invalid={Boolean(error)}
            label={`${parameter.label} value`}
            onCommit={onChange}
          />
        </div>
      );
      break;

    case "number":
      control = (
        <NumberField
          parameter={parameter}
          value={numeric}
          invalid={Boolean(error)}
          id={controlId}
          describedBy={describedBy}
          onCommit={onChange}
        />
      );
      break;

    case "switch":
      control = (
        <span className="switch">
          <input
            id={controlId}
            type="checkbox"
            aria-describedby={describedBy}
            // A checkbox has two states and the schema has three (true, false, absent). Until the
            // user touches it the key stays absent; after that an explicit value is always sent.
            checked={value === true}
            onChange={(event) => onChange(event.target.checked)}
          />
          <span className="stamp">{value === true ? "on" : "off"}</span>
        </span>
      );
      break;

    case "seed":
      control = (
        <SeedField
          parameter={parameter}
          value={numeric}
          invalid={Boolean(error)}
          id={controlId}
          describedBy={describedBy}
          onCommit={onChange}
        />
      );
      break;

    case "text":
      control = (
        <input
          id={controlId}
          type="text"
          className={error ? "input is-invalid" : "input"}
          aria-describedby={describedBy}
          maxLength={parameter.max_length}
          value={value === undefined ? "" : String(value)}
          onChange={(event) => onChange(event.target.value === "" ? undefined : event.target.value)}
        />
      );
      break;
  }

  const grouped = isGroupControl(shape);

  return (
    <div className="field">
      <div className="field-label">
        {grouped ? (
          <span id={labelId} className="stamp-value">
            {parameter.label}
          </span>
        ) : (
          <label id={labelId} htmlFor={controlId} className="stamp-value">
            {parameter.label}
          </label>
        )}
        {parameter.experimental && (
          <span className="badge-experimental" title={EXPERIMENTAL_TITLE}>
            Experimental
          </span>
        )}
      </div>
      {control}
      {parameter.description && (
        <p className="field-help" id={helpId}>
          {parameter.description}
        </p>
      )}
      {error && (
        <p className="field-error" id={errorId}>
          {error}
        </p>
      )}
    </div>
  );
}

// ── Groups ─────────────────────────────────────────────────────────────────────────────────

const GROUP_ORDER: ParameterGroup[] = ["output", "quality", "content", "advanced"];

const GROUP_LABEL: Record<ParameterGroup, string> = {
  output: "Output",
  quality: "Quality",
  content: "Content",
  advanced: "Advanced",
};

// ── ParameterControls ──────────────────────────────────────────────────────────────────────

interface ParameterControlsProps {
  model: Model;
  mode: GenerationMode;
  values: ParameterValues;
  errors?: Record<string, string>;
  onChange: (next: ParameterValues) => void;
}

export function ParameterControls({
  model,
  mode,
  values,
  errors,
  onChange,
}: ParameterControlsProps): ReactElement {
  const visible = useMemo(() => resolveVisible(model, mode, values), [model, mode, values]);

  const shown = model.parameters.filter((parameter) => visible.has(parameter.key));
  const standard = shown.filter((parameter) => !parameter.advanced);
  // Advanced is a property of the parameter, not of its group: an advanced output parameter is
  // still advanced, and hiding it behind the disclosure is the whole point of the flag.
  const advanced = shown.filter((parameter) => parameter.advanced);

  function set(key: string, next: Scalar | undefined): void {
    const updated: ParameterValues = { ...values };
    if (next === undefined) {
      delete updated[key];
    } else {
      updated[key] = next;
    }
    onChange(updated);
  }

  function renderGroups(parameters: Parameter[]): ReactElement[] {
    return GROUP_ORDER.flatMap<ReactElement>((group) => {
      const inGroup = parameters.filter((parameter) => parameter.group === group);
      if (inGroup.length === 0) return [];
      return [
        <section key={group}>
          <h3 className="param-group-title">{GROUP_LABEL[group]}</h3>
          <div className="param-fields">
            {inGroup.map((parameter) => (
              <ParameterField
                key={parameter.key}
                parameter={parameter}
                value={readValue(values, parameter.key)}
                error={errors?.[parameter.key]}
                onChange={(next) => set(parameter.key, next)}
              />
            ))}
          </div>
        </section>,
      ];
    });
  }

  if (shown.length === 0) {
    return <p className="field-help">This model takes nothing but the prompt.</p>;
  }

  return (
    <div className="param-groups">
      {renderGroups(standard)}
      {advanced.length > 0 && (
        <details>
          <summary className="param-group-title" style={{ cursor: "pointer" }}>
            Advanced ({advanced.length})
          </summary>
          <div className="param-groups">{renderGroups(advanced)}</div>
        </details>
      )}
    </div>
  );
}
